import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as review from '@/lib-backend/methods/review';
import {
    FakeReviewAuthor,
    FakeReviewAuthorAddDto,
    FakeReviewComment,
    FakeReviewCommentAddDto,
    GameReview,
    GameReviewKindFilter,
    RatingCounts,
    RatingStats,
    RemoveStrategy,
} from '@/lib-backend';
import { FieldUpdate } from '@/lib-backend/core/types';

export type AddOutcome =
    | { result: 'Ok'; insertedCount: number }
    | { result: 'Error' };

export type ListAuthorsOutcome =
    | { result: 'Ok'; authors: FakeReviewAuthor[] }
    | { result: 'Error' };

export type ListCommentsOutcome =
    | { result: 'Ok'; comments: FakeReviewComment[] }
    | { result: 'Error' };

export type UpdateAuthorOutcome =
    | { result: 'Ok'; author: FakeReviewAuthor }
    | { result: 'NotFound' }
    | { result: 'Error' };

export type UpdateCommentOutcome =
    | { result: 'Ok'; comment: FakeReviewComment }
    | { result: 'NotFound' }
    | { result: 'InvalidRatingRange' }
    | { result: 'Error' };

export type DeleteCommentOutcome =
    | { result: 'Ok'; deleted: boolean }
    | { result: 'Error' };

export type CommentsStatOutcome =
    | { result: 'Ok'; totalCount: number; perRating: RatingCounts }
    | { result: 'Error' };

export type GameReviewInfoOutcome =
    | { result: 'Ok'; stats: RatingStats }
    | { result: 'Error' };

export interface InsufficientAuthorsDetails {
    availableAuthors: number;
    requestedAuthors: number;
}

export interface InsufficientCommentsDetails {
    commentsRating: number;
    availableComments: number;
    requestedComments: number;
}

export type AddGameReviewsOutcome =
    | { result: 'Ok'; createdCount: number; stats: RatingStats }
    | ({ result: 'InsufficientAuthors' } & InsufficientAuthorsDetails)
    | ({ result: 'InsufficientComments' } & InsufficientCommentsDetails)
    | { result: 'Error' };

export type ReplaceGameReviewsOutcome =
    | {
          result: 'Ok';
          removedCount: number;
          createdCount: number;
          stats: RatingStats;
      }
    | ({ result: 'InsufficientAuthors' } & InsufficientAuthorsDetails)
    | ({ result: 'InsufficientComments' } & InsufficientCommentsDetails)
    | { result: 'Error' };

export type RemoveGameReviewsOutcome =
    | { result: 'Ok'; removedCount: number }
    | { result: 'Error' };

export type ListGameReviewsOutcome =
    | { result: 'Ok'; reviews: GameReview[] }
    | { result: 'Error' };

export type AddFakeGameReviewOutcome =
    | { result: 'Ok'; review: GameReview }
    | { result: 'InvalidAuthor' }
    | { result: 'AuthorAlreadyUsed' }
    | { result: 'Error' };

export type UpdateFakeGameReviewOutcome =
    | { result: 'Ok'; review: GameReview }
    | { result: 'NotFound' }
    | { result: 'WrongType' }
    | { result: 'InvalidAuthor' }
    | { result: 'AuthorAlreadyUsed' }
    | { result: 'Error' };

export type DeleteGameReviewOutcome =
    | { result: 'Ok' }
    | { result: 'NotFound' }
    | { result: 'WrongType' }
    | { result: 'Error' };

export interface AddFakeGameReviewParams {
    realmId: string;
    gameId: string;
    /** Fake author id from the author pool. */
    userId: string;
    rating: number;
    comment: string;
    createdAt: string;
}

export interface UpdateFakeGameReviewPatch {
    /** null = leave untouched */
    UserId: string | null;
    Rating: number | null;
    Comment: string | null;
    CreatedAt: string | null;
}

export interface GameReviewsParams {
    realmId: string;
    gameId: string;
    ratingCounts: RatingCounts;
    tags: string[];
    createdFrom: string;
    createdTo: string;
}

export interface UpdateAuthorPatch {
    /** null = leave untouched */
    Username: string | null;
    /** null = leave untouched, { value: x } = set (x can itself be null to clear) */
    AvatarUrl: FieldUpdate<string | null>;
    /** null = leave untouched */
    IsActive: boolean | null;
}

export interface UpdateCommentPatch {
    Text: string | null;
    Tags: string[] | null;
    MinRating: number | null;
    MaxRating: number | null;
}

@Injectable({ providedIn: 'root' })
export class FakeReviewService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    // ── Authors: add ────────────────────────────────────────────────────────

    async addAuthors(authors: FakeReviewAuthorAddDto[]): Promise<AddOutcome> {
        try {
            const resp = await this.api.client.call(
                review.addFakeAuthors({ Authors: authors }),
            );
            return { result: 'Ok', insertedCount: resp.InsertedCount };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    // ── Authors: list ───────────────────────────────────────────────────────

    async listAuthors(
        activeOnly: boolean,
        skip: number,
        limit: number,
    ): Promise<ListAuthorsOutcome> {
        try {
            const resp = await this.api.client.call(
                review.listFakeAuthors({
                    ActiveOnly: activeOnly,
                    Skip: skip,
                    Limit: limit,
                }),
            );
            return { result: 'Ok', authors: resp.Authors };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    // ── Authors: update ─────────────────────────────────────────────────────

    async updateAuthor(
        id: string,
        patch: UpdateAuthorPatch,
    ): Promise<UpdateAuthorOutcome> {
        try {
            const resp = await this.api.client.call(
                review.updateFakeAuthor({
                    Id: id,
                    Username: patch.Username,
                    AvatarUrl: patch.AvatarUrl,
                    IsActive: patch.IsActive,
                }),
            );
            if (resp.Result === 'Ok') {
                return { result: 'Ok', author: resp.Author };
            }
            if (resp.Result === 'NotFound') {
                return { result: 'NotFound' };
            }
            return { result: 'Error' };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    // ── Comments: add ───────────────────────────────────────────────────────

    async addComments(
        comments: FakeReviewCommentAddDto[],
    ): Promise<AddOutcome> {
        try {
            const resp = await this.api.client.call(
                review.addFakeComments({ Comments: comments }),
            );
            return { result: 'Ok', insertedCount: resp.InsertedCount };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    // ── Comments: list ──────────────────────────────────────────────────────

    async listComments(
        tags: string[] | null,
        skip: number,
        limit: number,
    ): Promise<ListCommentsOutcome> {
        try {
            const resp = await this.api.client.call(
                review.listFakeComments({
                    Tags: tags,
                    Skip: skip,
                    Limit: limit,
                }),
            );
            return { result: 'Ok', comments: resp.Comments };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    // ── Comments: update ────────────────────────────────────────────────────

    async updateComment(
        id: string,
        patch: UpdateCommentPatch,
    ): Promise<UpdateCommentOutcome> {
        try {
            const resp = await this.api.client.call(
                review.updateFakeComment({
                    Id: id,
                    Text: patch.Text,
                    Tags: patch.Tags,
                    MinRating: patch.MinRating,
                    MaxRating: patch.MaxRating,
                }),
            );
            if (resp.Result === 'Ok') {
                return { result: 'Ok', comment: resp.Comment };
            }
            if (resp.Result === 'NotFound') {
                return { result: 'NotFound' };
            }
            if (resp.Result === 'InvalidRatingRange') {
                return { result: 'InvalidRatingRange' };
            }
            return { result: 'Error' };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    // ── Comments: delete ────────────────────────────────────────────────────

    async deleteComment(id: string): Promise<DeleteCommentOutcome> {
        try {
            const resp = await this.api.client.call(
                review.deleteFakeComment({ Id: id }),
            );
            return { result: 'Ok', deleted: resp.Deleted };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    // ── Comments: stats ─────────────────────────────────────────────────────

    async getCommentsStat(tags: string[] | null): Promise<CommentsStatOutcome> {
        try {
            const resp = await this.api.client.call(
                review.getFakeCommentsStat({ Tags: tags }),
            );
            return {
                result: 'Ok',
                totalCount: resp.TotalCount,
                perRating: resp.PerRating,
            };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    // ── Game-level reviews ──────────────────────────────────────────────────

    async getGameReviewInfo(
        realmId: string,
        gameId: string,
    ): Promise<GameReviewInfoOutcome> {
        try {
            const resp = await this.api.client.call(
                review.getFakeReviewInfo({
                    RealmId: realmId,
                    GameId: gameId,
                }),
            );
            return { result: 'Ok', stats: resp.Stats };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    async addGameReviews(
        params: GameReviewsParams,
    ): Promise<AddGameReviewsOutcome> {
        try {
            const resp = await this.api.client.call(
                review.addFakeReviews({
                    RealmId: params.realmId,
                    GameId: params.gameId,
                    RatingCounts: params.ratingCounts,
                    Tags: params.tags,
                    CreatedFrom: params.createdFrom,
                    CreatedTo: params.createdTo,
                }),
            );
            if (resp.Result === 'Ok') {
                return {
                    result: 'Ok',
                    createdCount: resp.CreatedCount,
                    stats: resp.Stats,
                };
            }
            if (resp.Result === 'InsufficientAuthors') {
                return {
                    result: 'InsufficientAuthors',
                    availableAuthors: resp.AvailableAuthors,
                    requestedAuthors: resp.RequestedAuthors,
                };
            }
            if (resp.Result === 'InsufficientComments') {
                return {
                    result: 'InsufficientComments',
                    commentsRating: resp.CommentsRating,
                    availableComments: resp.AvailableComments,
                    requestedComments: resp.RequestedComments,
                };
            }
            return { result: 'Error' };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    async replaceGameReviews(
        params: GameReviewsParams,
    ): Promise<ReplaceGameReviewsOutcome> {
        try {
            const resp = await this.api.client.call(
                review.replaceFakeReviews({
                    RealmId: params.realmId,
                    GameId: params.gameId,
                    RatingCounts: params.ratingCounts,
                    Tags: params.tags,
                    CreatedFrom: params.createdFrom,
                    CreatedTo: params.createdTo,
                }),
            );
            if (resp.Result === 'Ok') {
                return {
                    result: 'Ok',
                    removedCount: resp.RemovedCount,
                    createdCount: resp.CreatedCount,
                    stats: resp.Stats,
                };
            }
            if (resp.Result === 'InsufficientAuthors') {
                return {
                    result: 'InsufficientAuthors',
                    availableAuthors: resp.AvailableAuthors,
                    requestedAuthors: resp.RequestedAuthors,
                };
            }
            if (resp.Result === 'InsufficientComments') {
                return {
                    result: 'InsufficientComments',
                    commentsRating: resp.CommentsRating,
                    availableComments: resp.AvailableComments,
                    requestedComments: resp.RequestedComments,
                };
            }
            return { result: 'Error' };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    async removeGameReviews(
        realmId: string,
        gameId: string,
        count: number,
        strategy: RemoveStrategy,
    ): Promise<RemoveGameReviewsOutcome> {
        try {
            const resp = await this.api.client.call(
                review.removeFakeReviews({
                    RealmId: realmId,
                    GameId: gameId,
                    Count: count,
                    Strategy: strategy,
                }),
            );
            return { result: 'Ok', removedCount: resp.RemovedCount ?? 0 };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    async removeAllGameReviews(
        realmId: string,
        gameId: string,
    ): Promise<RemoveGameReviewsOutcome> {
        try {
            const resp = await this.api.client.call(
                review.removeAllFakeReviews({
                    RealmId: realmId,
                    GameId: gameId,
                }),
            );
            return { result: 'Ok', removedCount: resp.RemovedCount };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    // ── Per-review moderation ───────────────────────────────────────────────

    async listGameReviews(
        realmId: string,
        gameId: string | null,
        filter: GameReviewKindFilter,
        skip: number,
        limit: number,
    ): Promise<ListGameReviewsOutcome> {
        try {
            const resp = await this.api.client.call(
                review.listGameReviews({
                    RealmId: realmId,
                    GameId: gameId,
                    Filter: filter,
                    Skip: skip,
                    Limit: limit,
                }),
            );
            return { result: 'Ok', reviews: resp.Reviews };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    async addFakeGameReview(
        params: AddFakeGameReviewParams,
    ): Promise<AddFakeGameReviewOutcome> {
        try {
            const resp = await this.api.client.call(
                review.addFakeGameReview({
                    RealmId: params.realmId,
                    GameId: params.gameId,
                    UserId: params.userId,
                    Rating: params.rating,
                    Comment: params.comment,
                    CreatedAt: params.createdAt,
                }),
            );
            if (resp.Result === 'Ok') {
                return { result: 'Ok', review: resp.Review };
            }
            if (resp.Result === 'InvalidAuthor') {
                return { result: 'InvalidAuthor' };
            }
            if (resp.Result === 'AuthorAlreadyUsed') {
                return { result: 'AuthorAlreadyUsed' };
            }
            return { result: 'Error' };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    async updateFakeGameReview(
        realmId: string,
        id: string,
        patch: UpdateFakeGameReviewPatch,
    ): Promise<UpdateFakeGameReviewOutcome> {
        try {
            const resp = await this.api.client.call(
                review.updateFakeGameReview({
                    RealmId: realmId,
                    Id: id,
                    UserId: patch.UserId,
                    Rating: patch.Rating,
                    Comment: patch.Comment,
                    CreatedAt: patch.CreatedAt,
                }),
            );
            if (resp.Result === 'Ok') {
                return { result: 'Ok', review: resp.Review };
            }
            if (resp.Result === 'NotFound') {
                return { result: 'NotFound' };
            }
            if (resp.Result === 'WrongType') {
                return { result: 'WrongType' };
            }
            if (resp.Result === 'InvalidAuthor') {
                return { result: 'InvalidAuthor' };
            }
            if (resp.Result === 'AuthorAlreadyUsed') {
                return { result: 'AuthorAlreadyUsed' };
            }
            return { result: 'Error' };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    async deleteFakeGameReview(
        realmId: string,
        id: string,
    ): Promise<DeleteGameReviewOutcome> {
        try {
            const resp = await this.api.client.call(
                review.deleteFakeGameReview({ RealmId: realmId, Id: id }),
            );
            return this.mapDeleteResult(resp.Result);
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    async deleteRealGameReview(
        realmId: string,
        id: string,
    ): Promise<DeleteGameReviewOutcome> {
        try {
            const resp = await this.api.client.call(
                review.deleteRealGameReview({ RealmId: realmId, Id: id }),
            );
            return this.mapDeleteResult(resp.Result);
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Error' };
        }
    }

    private mapDeleteResult(
        result: 'Unknown' | 'Ok' | 'NotFound' | 'WrongType',
    ): DeleteGameReviewOutcome {
        if (result === 'Ok') return { result: 'Ok' };
        if (result === 'NotFound') return { result: 'NotFound' };
        if (result === 'WrongType') return { result: 'WrongType' };
        return { result: 'Error' };
    }
}
